import { Icons } from "@stacc/flow-ui-components";
import { ApplicantCollection } from "../../../components/Applicant/ApplicantCollection";
import { RadioButtonGroup } from "../../../components/Decision/RadioButtonGroup";
import { InfoRow } from "../../../components/InfoRow/InfoRow";
import { FormText } from "../../../components/Input/FormText";
import { FormTextArea } from "../../../components/Input/FormTextArea";
import { SelectGroup } from "../../../components/Select/SelectGroup";
import {
  type CommonData,
  Country,
  InsuranceOptions
} from "../../../types/common";
import { formatAmount } from "../../../utils/helperFunctions";
import {
  ApplicantDetailsLabel,
  ApplicantDetailsWrapper,
  LabelBold
} from "../manual-check-final/styles";
import { FlexAlignCenter } from "../manual-id-review/style";
import * as Styles from "../styles";

interface FormConfigProps {
  errorMessages: string | null;
  applicant: any;
  coapplicant: any;
  decision: any;
  insuranceSale: any;
  autogiroAccountNumber?: string;
  disbursementAccountNumber?: string;
  handleAutogiroAccountNumberChange: (value: any) => void;
  handleDisbursementAccountNumberChange: (value: any) => void;
  handleInsuranceSaleChange: (value: any) => void;
  handleDecisionChange: (value: any) => void;
  handleNoteChange: (value: any) => void;
  comment: string;
  data: CommonData;
  status: string;
}

export const formConfig = ({
  errorMessages,
  applicant,
  coapplicant,
  decision,
  insuranceSale,
  autogiroAccountNumber,
  disbursementAccountNumber,
  handleAutogiroAccountNumberChange,
  handleDisbursementAccountNumberChange,
  handleDecisionChange,
  handleNoteChange,
  handleInsuranceSaleChange,
  comment,
  data,
  status
}: FormConfigProps) => {
  const {
    finalLoanAmount,
    interestRate,
    effectiveInterestRate,
    maxLoanAmount,
    minLoanAmount,
    startupFee,
    termFee,
    terms,
    monthlyCost,
    refinanceAmount,
    topUpAmount,
    cashPayoutAmount,
    insuranceCost,
    insuranceSelected
  } = data?.offer || {};

  const country = data?.country;
  const addressInformationSe = data?.integrations?.uc?.addressInformation;
  const addressSe = addressInformationSe
    ? `${addressInformationSe.streetName}, ${addressInformationSe.postcode}, ${addressInformationSe.municipality}`
    : "Address not available";

  const addressInformationFi =
    data?.integrations?.bisnode?.dvvFinance?.applicant?.response
      ?.consumerInformation?.registeredAddresses?.[0];
  const addressFi = addressInformationFi
    ? `${addressInformationFi.streetAddress}, ${addressInformationFi.postalCode}, ${addressInformationFi.postOffice}`
    : "Address not available";

  const address = country === Country.SE ? addressSe : addressFi;

  const offerGivenIcon = finalLoanAmount ? (
    <Icons.SuccessCircle />
  ) : status === "active" ? (
    <Icons.ActiveCircle />
  ) : (
    <Icons.AbortedCircle />
  );

  const applicationDetailsOptions = [
    { label: "Final loan amount", value: finalLoanAmount },
    ...(topUpAmount
      ? [{ label: "Internal loan amount", value: topUpAmount }]
      : []),
    { label: "Cash payout amount", value: cashPayoutAmount },
    { label: "Refinance amount", value: refinanceAmount },
    { label: "Interest rate", value: interestRate },
    { label: "Maximum loan amount", value: maxLoanAmount },
    { label: "Minimum loan amount", value: minLoanAmount },
    { label: "Startup fee", value: startupFee },
    { label: "Term fee", value: termFee },
    { label: "Terms", value: terms },
    { label: "Effective interest rate", value: effectiveInterestRate },
    { label: "Monthly cost", value: monthlyCost },
    { label: "Offer given", value: offerGivenIcon },
    {
      label: "Insurance Cost",
      value: formatAmount(insuranceCost ?? 0, data?.country)
    },
    { label: "Insurance Choosen", value: insuranceSelected ? "Yes" : "No" }
  ];

  const components = [
    {
      // Applicant details
      component: (
        <>
          <ApplicantDetailsWrapper>
            <ApplicantDetailsLabel>Applicant details</ApplicantDetailsLabel>
            <ApplicantCollection {...applicant} />
            {coapplicant && <ApplicantCollection {...coapplicant} />}
          </ApplicantDetailsWrapper>
        </>
      ),
      order: 1
    },
    // Address details
    {
      component: (
        <div
          style={{
            padding: "0 28px"
          }}
        >
          <InfoRow
            value={address}
            title="Address"
            hasMatch={false}
            hasPadding={false}
          />
          <InfoRow
            value={
              data?.integrations?.LeaBankWeb?.customerInformation
                ?.accountNumber ??
              data?.agentApiApplication?.application?.bankAccountNumber ??
              ""
            }
            title="Account number"
            hasMatch={false}
            hasPadding={false}
          />
        </div>
      ),
      order: 2
    },
    // Application details
    {
      component: (
        <>
          <ApplicantDetailsLabel>Application details</ApplicantDetailsLabel>
          <div
            style={{
              padding: "0 28px"
            }}
          >
            {applicationDetailsOptions.map((option, index) => (
              <InfoRow
                value={option.value}
                title={option.label}
                key={`infoRow${option.label}`}
                hasMatch={false}
                hasPadding={false}
              />
            ))}
          </div>
        </>
      ),
      order: 3
    },
    // Account number
    {
      component: (
        <FormText
          defaultValue={autogiroAccountNumber}
          label="Autogiro account number"
          onChange={(e: string) => handleAutogiroAccountNumberChange(e)}
          name="autogiro-account-number"
          required
        />
      ),
      order: 5
    },
    // Disbursement account number
    {
      component: (
        <FormText
          defaultValue={disbursementAccountNumber}
          label="Disbursement account number"
          onChange={(e: string) => handleDisbursementAccountNumberChange(e)}
          name="disbursement-account-number"
          required
        />
      ),
      order: 6
    },
    // Decision
    {
      component: (
        <RadioButtonGroup
          title="Decision"
          id="approved"
          radioButtonValue={decision}
          options={[
            { label: "Approve application", value: true },
            { label: "Reject application", value: false }
          ]}
          onChange={(e: any) => handleDecisionChange(e)}
        />
      ),
      order: 7
    },
    // Internal comment
    {
      component: (
        <>
          <FormTextArea
            label="Internal comment"
            name="internal-comment"
            onChange={(e: any) => handleNoteChange(e)}
            defaultValue={comment}
            placeholder="Other applicant information..."
          />
          {errorMessages && (
            <Styles.ErrorMessageBox>
              <FlexAlignCenter>
                <LabelBold>{errorMessages}</LabelBold>
              </FlexAlignCenter>
            </Styles.ErrorMessageBox>
          )}
        </>
      ),
      order: 8
    }
  ];

  if (!insuranceSelected) {
    //Insurance sale if insurance is not selected
    components.push({
      component: (
        <SelectGroup
          title="Insurance sale"
          id="insuranceSale"
          value={insuranceSale}
          options={[
            {
              label: "Without insurance",
              value: InsuranceOptions.WITHOUT_INSURANCE
            },
            { label: "With insurance", value: InsuranceOptions.WITH_INSURANCE },
            { label: "No answer", value: InsuranceOptions.NO_ANSWER },
            { label: "Not qualified", value: InsuranceOptions.NOT_QUALIFIED }
          ]}
          onChange={(value: any) => handleInsuranceSaleChange(value)}
        />
      ),
      order: 4
    });
  }

  return components;
};
